import React, { useState, useEffect } from 'react';
import { getAuthToken, getAuthHeaders } from '../../../utils/auth';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const HistoryModule = ({ user }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState(null);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchHistory();
  }, []);

  const fetchHistory = async () => {
    try {
      const response = await fetch(`${API_URL}/api/user/content`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setHistory(data);
      }
    } catch (error) {
      console.error('Error fetching history:', error);
    } finally {
      setLoading(false);
    }
  };

  const filters = [
    { id: 'all', name: 'Tout', icon: '📋' },
    { id: 'blog', name: 'Blog', icon: '📝' },
    { id: 'social', name: 'Social', icon: '📱' },
    { id: 'email', name: 'Email', icon: '📧' },
    { id: 'ad', name: 'Publicité', icon: '📢' }
  ];

  const filteredHistory = filter === 'all' 
    ? history 
    : history.filter(item => item.content_type === filter);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', { 
      day: 'numeric', 
      month: 'short', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="grid grid-cols-12 gap-6 h-full">
      {/* Sidebar Filters */}
      <div className="col-span-2 space-y-2">
        <h3 className="text-white font-semibold mb-4">Filtres</h3>
        {filters.map(f => (
          <button
            key={f.id}
            onClick={() => setFilter(f.id)}
            className={`w-full text-left p-3 rounded-xl transition-all flex items-center space-x-2 ${
              filter === f.id
                ? 'bg-gray-700 text-white'
                : 'text-gray-400 hover:bg-gray-800 hover:text-white'
            }`}
          >
            <span>{f.icon}</span>
            <span>{f.name}</span>
            <span className="ml-auto text-xs text-gray-500">
              {f.id === 'all' ? history.length : history.filter(h => h.content_type === f.id).length}
            </span>
          </button>
        ))}
      </div>

      {/* History List */}
      <div className="col-span-4 bg-[#12121a] rounded-2xl border border-gray-800 overflow-hidden flex flex-col">
        <div className="p-4 border-b border-gray-800">
          <h3 className="text-white font-semibold">Historique</h3>
          <p className="text-xs text-gray-500">{filteredHistory.length} éléments</p>
        </div>
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="p-8 text-center text-gray-500">
              <div className="w-8 h-8 border-2 border-gray-600 border-t-white rounded-full animate-spin mx-auto mb-2"></div>
              Chargement...
            </div>
          ) : filteredHistory.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <span className="text-4xl block mb-2">📭</span>
              Aucun contenu
            </div>
          ) : (
            <div className="divide-y divide-gray-800">
              {filteredHistory.map((item, idx) => (
                <button
                  key={item.id || idx}
                  onClick={() => setSelectedItem(item)}
                  className={`w-full text-left p-4 transition-all hover:bg-gray-800/50 ${
                    selectedItem?.id === item.id ? 'bg-gray-800/50' : ''
                  }`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-green-400 font-medium uppercase">{item.content_type}</span>
                    <span className="text-xs text-gray-500">{formatDate(item.created_at)}</span>
                  </div>
                  <p className="text-sm text-white truncate">{item.topic}</p>
                  <p className="text-xs text-gray-500 truncate mt-1">{item.content?.substring(0, 100)}...</p>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Content Preview */}
      <div className="col-span-6 bg-[#12121a] rounded-2xl border border-gray-800 overflow-hidden flex flex-col">
        <div className="p-4 border-b border-gray-800 flex items-center justify-between">
          <div>
            <h3 className="text-white font-semibold">Aperçu</h3>
            {selectedItem && (
              <p className="text-xs text-gray-500">{selectedItem.topic}</p>
            )}
          </div>
          {selectedItem && (
            <div className="flex items-center space-x-2">
              <button
                onClick={() => navigator.clipboard.writeText(selectedItem.content)}
                className="p-2 hover:bg-gray-800 rounded-lg transition-colors text-gray-400 hover:text-white"
                title="Copier"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"/>
                </svg>
              </button>
              <button className="p-2 hover:bg-gray-800 rounded-lg transition-colors text-gray-400 hover:text-white" title="Exporter">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"/>
                </svg>
              </button>
            </div>
          )}
        </div>
        <div className="flex-1 p-4 overflow-y-auto">
          {selectedItem ? (
            <div className="prose prose-invert max-w-none">
              <div className="mb-4 flex items-center space-x-4 text-xs text-gray-500">
                <span className="px-2 py-1 bg-gray-800 rounded">{selectedItem.content_type}</span>
                <span>{selectedItem.tone}</span>
                <span>{selectedItem.word_count} mots demandés</span>
              </div>
              <div className="whitespace-pre-wrap text-gray-300 leading-relaxed">
                {selectedItem.content}
              </div>
            </div>
          ) : (
            <div className="h-full flex items-center justify-center text-gray-500">
              <div className="text-center">
                <span className="text-4xl block mb-2">👈</span>
                <p>Sélectionnez un élément<br/>pour voir le contenu</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HistoryModule;
